import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { AccountChanges, AccountView, Connections, LoginView, PlaidLinkStart, ServiceConnection } from '../../../services/src/shared/api-types.ts';
import { hasNewAccounts, isDisconnected, isFailing, isNotShared, isRemoved, needsAttention } from '../../../services/src/shared/summary.ts';
import type { ScreenProps } from '../App.tsx';
import type { Api } from '../api.ts';
import { maskLabel, monthDay, num, ordinal, plural, shortStamp, when } from '../format.ts';
import {
  clearLink, friendlyLinkError, leavePlaidReturn, loadPlaid, readLink, saveLink, takePlaidReturn, type PlaidHandler, type PlaidLinkError, type SavedLink,
} from '../plaid.ts';
import { SEED_LOGINS } from '../seed-logins.ts';
import { Empty, ErrorBanner, Loading, Stat } from '../ui.tsx';

type Show = 'all' | 'attention' | 'cards' | 'bank';

const SHOW: { id: Show; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'cards', label: 'Cards' },
  { id: 'bank', label: 'Bank accounts' },
];

const KIND_LABELS: Record<AccountView['kind'], string> = { card: 'Card', checking: 'Checking', savings: 'Savings', balance: 'Balance', other: 'Other' };

/** A login's status as a short phrase and a tone. */
function loginStatus(l: LoginView): { text: string; tone: 'ok' | 'warn' | 'off' | 'err' } {
  if (l.source === 'config') return { text: 'Not connected', tone: 'off' };
  if (isDisconnected(l)) return { text: 'Disconnected', tone: 'off' };
  if (isRemoved(l)) return { text: 'Removed', tone: 'off' };
  if (l.needsSignIn) return { text: 'Sign in again', tone: 'warn' };
  if (l.status === 'error') return { text: 'Not syncing', tone: 'err' };
  if (l.status === 'pending') return { text: 'Waiting for first sync', tone: 'off' };
  return { text: 'Syncing', tone: 'ok' };
}

function loginMeta(l: LoginView): string {
  if (l.note && l.source === 'config') return l.note;
  const parts: string[] = [];
  if (l.note) parts.push(l.note);
  if (l.needsSignIn) parts.push(l.lastSync ? `Last updated ${monthDay(l.lastSync)}` : 'Never synced');
  else if (l.status === 'error') parts.push(l.error ?? 'Failing');
  else if (l.lastSync) parts.push(`updated ${shortStamp(l.lastSync)}`);
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function accountStatus(l: LoginView, a: AccountView): { text: string; tone: string } {
  if (isNotShared(a)) return { text: a.notSharedSince ? `Not shared since ${monthDay(a.notSharedSince)}` : 'Not shared', tone: 'off' };
  const s = loginStatus(l);
  if (l.needsSignIn) return { text: `Stopped ${monthDay(a.lastLineDate ?? l.lastSync)}`, tone: 'warn' };
  return { text: s.text === 'Waiting for first sync' ? 'Waiting' : s.text, tone: s.tone };
}

const isCard = (a: AccountView) => a.kind === 'card';

export function ConnectionsScreen({ api, refreshSummary, mock }: ScreenProps) {
  const [data, setData] = useState<Connections | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Plaid Link is being prepared: 'new', or the itemId being reconnected. Drives the buttons' busy label. */
  const [opening, setOpening] = useState<string | null>(null);
  /** Mock mode's stand-in for Plaid Link. */
  const [mockLink, setMockLink] = useState<SavedLink | null>(null);
  /** "Send a link instead": Plaid's hosted page, for someone else to open. */
  const [hosted, setHosted] = useState<PlaidLinkStart | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [show, setShow] = useState<Show>('all');
  const [search, setSearch] = useState('');
  /** The login whose disconnect (or dev purge) is waiting for a yes. */
  const [confirming, setConfirming] = useState<{ id: string; action: 'disconnect' | 'purge' } | null>(null);
  const searchId = useId();
  const handler = useRef<PlaidHandler | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.connections().then(setData, (e: Error) => setError(e.message));
  }, [api]);
  useEffect(load, [load]);
  useEffect(() => () => handler.current?.destroy(), []);
  /** The first sync saves a new login's accounts a few seconds after the exchange returns, so look again a few times. */
  const followUps = useRef<number[]>([]);
  const loadSoon = useCallback(() => {
    followUps.current.forEach((t) => window.clearTimeout(t));
    followUps.current = [3000, 8000, 20000].map((ms) =>
      window.setTimeout(() => {
        load();
        refreshSummary();
      }, ms),
    );
  }, [load, refreshSummary]);
  useEffect(() => () => followUps.current.forEach((t) => window.clearTimeout(t)), []);

  /** Link closed, either way: forget the saved token and leave /plaid-return. */
  const closeLink = () => {
    clearLink();
    leavePlaidReturn();
    setOpening(null);
    setMockLink(null);
    // Destroyed after Plaid's own callback returns.
    const done = handler.current;
    handler.current = null;
    if (done) window.setTimeout(() => done.destroy(), 0);
  };

  const linkSucceeded = async (link: SavedLink, publicToken: string, institutionName?: string) => {
    closeLink();
    setBusy(true);
    setMessage(link.mode === 'update' ? `Saving ${link.institution ?? 'the login'}…` : 'Saving the new login…');
    try {
      if (link.mode === 'update' && link.itemId) {
        const done = await api.plaidLinkUpdated(link.itemId, link.linkToken);
        setMessage(
          done.accounts
            ? `${done.reconnected.institution}: ${changesText(done.accounts)}`
            : `Reconnected ${done.reconnected.institution}. Pulling transactions now.`,
        );
      } else {
        const done = await api.exchangePlaidLink(publicToken, link.linkToken, institutionName);
        setMessage(`Connected ${done.linked.institution}. Pulling transactions now.`);
      }
      load();
      refreshSummary();
      loadSoon();
    } catch (e) {
      setMessage(`The bank signed in, but saving it failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const linkExited = (err: PlaidLinkError | null) => {
    closeLink();
    if (err) setMessage(friendlyLinkError(err)); // a plain close says nothing
  };

  /** Opens Plaid Link in the page (or mock mode's dialog). `receivedRedirectUri` resumes after an OAuth bank. */
  const openLink = async (link: SavedLink, receivedRedirectUri?: string) => {
    if (mock) {
      setMockLink(link);
      setOpening(null);
      return;
    }
    const Plaid = await loadPlaid();
    handler.current?.destroy();
    handler.current = Plaid.create({
      token: link.linkToken,
      ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
      onSuccess: (publicToken, metadata) => void linkSucceeded(link, publicToken, metadata.institution?.name),
      onExit: (err) => linkExited(err),
      onEvent: (name) => {
        if (name === 'OPEN' || name === 'HANDOFF') setOpening(null);
      },
    });
    handler.current.open();
    // Link's frame takes a moment; the busy label stays until it opens, at most a few seconds.
    window.setTimeout(() => setOpening(null), 6000);
  };

  /** Connect a bank, reconnect a login in update mode, or (manageAccounts) let the owner choose which accounts it shares. */
  const start = async (login?: LoginView, manageAccounts = false) => {
    setOpening(login?.id ?? 'new');
    setMessage(null);
    try {
      // The token and Plaid's script load side by side.
      const request = login ? { itemId: login.id, ...(manageAccounts ? { manageAccounts: true } : {}) } : {};
      const [started] = await Promise.all([api.startPlaidLink(request), mock ? null : loadPlaid()]);
      const link: SavedLink = {
        linkToken: started.linkToken,
        mode: login ? 'update' : 'new',
        ...(login ? { itemId: login.id, institution: login.institution } : {}),
        ...(login && manageAccounts ? { manageAccounts: true } : {}),
        expires: started.expires,
      };
      saveLink(link); // an OAuth bank leaves the page and comes back to /plaid-return
      await openLink(link);
    } catch (e) {
      closeLink();
      setMessage((e as Error).message);
    }
  };

  // Back from an OAuth bank's own site: pick Link up where it left off.
  useEffect(() => {
    const href = takePlaidReturn();
    if (!href) return;
    const saved = readLink();
    if (!saved) {
      leavePlaidReturn();
      setMessage("That bank sign-in can't be picked up again here. Start it again with Connect a bank.");
      return;
    }
    setOpening(saved.itemId ?? 'new');
    openLink(saved, href).catch((e: Error) => {
      closeLink();
      setMessage(e.message);
    });
    // Once per page load; takePlaidReturn guards against StrictMode's second run.
  }, []);

  /** Plaid's hosted page as a URL to send the account owner. */
  const sendLink = async () => {
    setBusy(true);
    setMessage(null);
    setCopied(false);
    try {
      setHosted(await api.startPlaidLink({ mode: 'hosted' }));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!hosted?.url) return;
    try {
      await navigator.clipboard.writeText(hosted.url);
      setCopied(true);
    } catch {
      setMessage('Copying was blocked. Select the link and copy it by hand.');
    }
  };

  const finish = async () => {
    if (!hosted) return;
    setBusy(true);
    try {
      const done = await api.finishPlaidLink(hosted.linkToken);
      if (done.reconnected) {
        setMessage(`Checking ${done.reconnected.institution} now. Its status updates when the sync finishes.`);
        setHosted(null);
      } else {
        setMessage(done.linked.length ? `Saved ${done.linked.map((l) => l.institution).join(', ')}. The first sync has started.` : 'No completed logins on that page yet.');
        if (done.linked.length) setHosted(null);
      }
      load();
      refreshSummary();
      loadSoon();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const linkBusy = busy || opening !== null || mockLink !== null;

  /** Disconnect stops syncing a login and keeps its records; purge (dev only) deletes a disconnected login's test data. */
  const act = async (login: LoginView, action: 'disconnect' | 'purge') => {
    setBusy(true);
    setMessage(null);
    try {
      if (action === 'disconnect') {
        const done = (await api.disconnectPlaidItem(login.id)).disconnected;
        const left = done.superseded ? ` ${plural(done.superseded, 'review item')} for it left the queue.` : '';
        setMessage(`Disconnected ${done.institution}. Its accounts and lines stay for the record.${left}`);
      } else {
        const done = (await api.purgePlaidItem(login.id)).purged;
        setMessage(`Deleted ${done.institution}'s test data: ${plural(done.accounts, 'account')}, ${plural(done.lines, 'line')}.`);
      }
      setConfirming(null);
      load();
      refreshSummary();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.logins
      .filter((l) => !isDisconnected(l))
      .filter((l) => show !== 'attention' || needsAttention(l))
      .map((l) => {
        const instHit = needle && l.institution.toLowerCase().includes(needle);
        const accounts = l.accounts.filter(
          (a) =>
            (show !== 'cards' || isCard(a)) &&
            (show !== 'bank' || !isCard(a)) &&
            (!needle || instHit || a.name.toLowerCase().includes(needle) || (a.mask ?? '').includes(needle) || (a.cardholder ?? '').toLowerCase().includes(needle)),
        );
        return { login: l, accounts };
      })
      .filter((g) => g.accounts.length > 0 || (show === 'all' && !needle && g.login.accounts.length === 0));
  }, [data, show, search]);

  if (error && !data) {
    return (
      <div className="page wide">
        <Head />
        <ErrorBanner message={error} onRetry={load} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page wide">
        <Head />
        <Loading kind="table" />
      </div>
    );
  }

  const t = data.totals;
  const plaidLogins = data.logins.filter((l) => l.source === 'plaid' && !isRemoved(l)).length;
  const attention = data.logins.filter(needsAttention);
  const service = (name: ServiceConnection['name']) => data.services.find((s) => s.name === name);
  const disconnected = data.logins.filter(isDisconnected);

  return (
    <div className="page wide">
      <Head
        lede={`${plural(t.accounts, 'account')} across ${plural(plaidLogins, 'bank login')}. Bank passwords go to Plaid, never to this app.`}
        sync={data.lastSync ? `Last sync ${when(data.lastSync.at)}${data.lastSync.ok ? '' : ' failed'}: ${data.lastSync.summary}` : 'No sync has run yet.'}
        action={
          <div className="head-actions">
            <button type="button" className="link-btn" disabled={linkBusy} onClick={sendLink}>
              Send a link instead
            </button>
            <button type="button" className="btn btn-primary" disabled={linkBusy} aria-busy={opening === 'new'} onClick={() => start()}>
              <BusyLabel busy={opening === 'new'}>Connect a bank</BusyLabel>
            </button>
          </div>
        }
      />

      {hosted?.url && (
        <div className="card connect-panel" role="region" aria-label="Link to send">
          <div className="send-link">
            <p>
              Send this to the account owner. They open it, connect their bank on Plaid's page, and then an admin clicks I've finished here. The link
              expires {when(hosted.expires)}.
            </p>
            <div className="copy-row">
              <input type="text" readOnly value={hosted.url} aria-label="Plaid link to send" onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="btn" onClick={copy}>
                <BusyLabel busy={copied} busyText="Copied">
                  Copy
                </BusyLabel>
              </button>
            </div>
          </div>
          <div className="buttons">
            <button type="button" className="btn" disabled={busy} onClick={() => setHosted(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={finish}>
              I've finished
            </button>
          </div>
        </div>
      )}
      {mockLink && (
        <MockLinkDialog
          link={mockLink}
          // New logins: the chosen seed user rides in the mock public token, as the mock server expects.
          onSuccess={(choice) =>
            void (mockLink.mode === 'new'
              ? linkSucceeded(mockLink, `public-sandbox-mock-${choice}-${Date.now()}`, SEED_LOGINS.find((s) => s.user === choice)?.institution)
              : linkSucceeded(mockLink, `public-sandbox-mock-${Date.now()}`, choice))
          }
          onExit={() => linkExited(null)}
        />
      )}
      <p className="notice" role="status">
        {message}
      </p>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {attention.map((l) =>
        !isFailing(l) && hasNewAccounts(l) ? (
          <div className="banner" role="status" key={l.id}>
            <div>
              <strong>{l.institution} has a new account to add</strong>
              <span>
                The bank reported it {monthDay(l.newAccountsAt!)}, often a new or reissued card. Its charges come in once the owner shares it in Manage
                accounts. {accountsPhrase(l.accounts)} keep syncing meanwhile.
              </span>
            </div>
            <button type="button" className="btn btn-dark" disabled={linkBusy} aria-busy={opening === l.id} onClick={() => start(l, true)}>
              <BusyLabel busy={opening === l.id}>Manage accounts</BusyLabel>
            </button>
          </div>
        ) : (
        <div className="banner" role="alert" key={l.id}>
          <div>
            <strong>{l.needsSignIn ? `${l.institution} needs you to sign in again` : `${l.institution} isn't syncing`}</strong>
            <span>
              {l.lastSync ? `Lines from ${accountsPhrase(l.accounts)} stopped coming in on ${monthDay(l.lastSync)}.` : `No lines have come in from ${accountsPhrase(l.accounts)} yet.`}
              {!l.needsSignIn && l.error ? ` ${l.error}` : ''}
            </span>
          </div>
          {l.source === 'plaid' && (
            <button type="button" className="btn btn-dark" disabled={linkBusy} aria-busy={opening === l.id} onClick={() => start(l)}>
              <BusyLabel busy={opening === l.id}>Reconnect</BusyLabel>
            </button>
          )}
        </div>
        ),
      )}

      <div className="stats tight">
        <Stat label="Syncing" value={num(t.syncing)} />
        <Stat label="Need attention" value={num(t.attention)} warn={t.attention > 0} />
        <Stat label="Not connected" value={num(t.notConnected)} />
        <Stat label="Lines this year" value={num(t.linesThisYear)} note={`Since ${monthDay(data.fiscalYearStart)}`} />
      </div>

      <div className="toolbar">
        <div role="group" aria-label="Show" className="pills">
          {SHOW.map((s) => (
            <button key={s.id} type="button" className="pill" aria-pressed={show === s.id} onClick={() => setShow(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        <label className="search" htmlFor={searchId}>
          <span className="visually-hidden">Search accounts</span>
          <input id={searchId} type="search" placeholder="Search card name or last 4" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>

      {data.logins.length === 0 ? (
        <Empty>No banks connected yet. Use Connect a bank to sign in through Plaid, or send the account owner a link.</Empty>
      ) : groups.length === 0 ? (
        <Empty>No accounts match.</Empty>
      ) : (
        <div className="card table-card">
          <table className="table min-720">
            <caption className="visually-hidden">Accounts by bank login</caption>
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Type</th>
                <th scope="col">Cardholder</th>
                <th scope="col" className="num">
                  Lines this year
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            {groups.map(({ login, accounts }) => {
              const s = loginStatus(login);
              return (
                <tbody key={login.id}>
                  <tr className="group-row">
                    <th scope="rowgroup" colSpan={5}>
                      <div className="group-line">
                        <span className="group-name">
                          <strong>{login.institution}</strong>
                          <span>{loginMeta(login)}</span>
                        </span>
                        <span className={`group-status st-${s.tone}`}>
                          {s.text}
                          {hasNewAccounts(login) && !login.needsSignIn && (
                            <>
                              <span className="st-warn">New account available</span>
                              <button
                                type="button"
                                className="btn"
                                disabled={linkBusy}
                                aria-busy={opening === login.id}
                                onClick={() => void start(login, true)}
                                aria-label={`Manage accounts for ${login.institution}`}
                              >
                                <BusyLabel busy={opening === login.id}>Manage accounts</BusyLabel>
                              </button>
                            </>
                          )}
                          {login.needsSignIn && login.source === 'plaid' && (
                            <button
                              type="button"
                              className="btn"
                              disabled={linkBusy}
                              aria-busy={opening === login.id}
                              onClick={() => start(login)}
                              aria-label={`Reconnect ${login.institution}`}
                            >
                              <BusyLabel busy={opening === login.id}>Reconnect</BusyLabel>
                            </button>
                          )}
                          {login.source === 'plaid' && !isRemoved(login) && (
                            <RowMenu
                              label={`More for ${login.institution}`}
                              disabled={linkBusy}
                              items={[
                                { label: 'Manage accounts', onSelect: () => void start(login, true) },
                                ...(login.needsSignIn ? [{ label: 'Reconnect', onSelect: () => void start(login) }] : []),
                                { label: 'Disconnect…', danger: true, onSelect: () => setConfirming({ id: login.id, action: 'disconnect' }) },
                              ]}
                            />
                          )}
                        </span>
                      </div>
                    </th>
                  </tr>
                  {confirming?.id === login.id && confirming.action === 'disconnect' && (
                    <tr className="confirm-row">
                      <td colSpan={5}>
                        <Confirm
                          text={`Stop syncing ${login.institution}? Its ${plural(login.accounts.length, 'account')} and their lines stay for the record.`}
                          yes="Disconnect"
                          busy={busy}
                          onYes={() => void act(login, 'disconnect')}
                          onNo={() => setConfirming(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {accounts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted">
                        No accounts yet.
                      </td>
                    </tr>
                  )}
                  {accounts.map((a) => {
                    const as = accountStatus(login, a);
                    return (
                      <tr key={a.accountId} className={isNotShared(a) ? 'not-shared' : undefined}>
                        <td>
                          {a.name} {a.mask && <span className="mask">{maskLabel(a.mask)}</span>}
                          {a.xeroFeed && (
                            <span className="chip xero" title="Checks and wires clear through this account. Xero also has its own bank feed of it.">
                              Xero feed
                            </span>
                          )}
                          {a.statement && !isNotShared(a) && <StatementDays api={api} account={a} onSaved={load} />}
                        </td>
                        <td className="acct">{KIND_LABELS[a.kind]}</td>
                        <td className="acct">{a.cardholder ?? <span className="muted">Not given</span>}</td>
                        <td className="num">{a.linesThisYear === null ? '—' : num(a.linesThisYear)}</td>
                        <td className={`acct st-${as.tone}`}>{as.text}</td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>
      )}

      {disconnected.length > 0 && (
        <details className="card disconnected">
          <summary>Disconnected ({disconnected.length})</summary>
          {disconnected.map((l) => (
            <div className="disconnected-login" key={l.id}>
              <div className="system">
                <div>
                  <strong>{l.institution}</strong>
                  <span className="small muted">
                    {accountsPhrase(l.accounts)}
                    {l.disconnectedAt ? ` · disconnected ${monthDay(l.disconnectedAt)}` : ''}
                    {l.disconnectedBy ? ` by ${l.disconnectedBy}` : ''}
                  </span>
                </div>
                {data.purgeAllowed && (
                  <RowMenu
                    label={`More for ${l.institution}`}
                    disabled={busy}
                    items={[{ label: 'Remove test data…', danger: true, onSelect: () => setConfirming({ id: l.id, action: 'purge' }) }]}
                  />
                )}
              </div>
              {data.purgeAllowed && confirming?.id === l.id && confirming.action === 'purge' && (
                <div className="confirm-row">
                  <Confirm
                    text={`Delete ${l.institution}'s test lines from dev? This can't be undone.`}
                    yes="Delete"
                    danger
                    busy={busy}
                    onYes={() => void act(l, 'purge')}
                    onNo={() => setConfirming(null)}
                  />
                </div>
              )}
            </div>
          ))}
        </details>
      )}

      <section className="systems" aria-labelledby="systems-h">
        <h2 id="systems-h">Systems</h2>
        <div className="card">
          <SystemRow name="Salesforce" what="Reads vendor payments, read-only" service={service('salesforce')} />
          <SystemRow name="Xero" what="Posts approved matches" service={service('xero')} />
        </div>
      </section>
    </div>
  );
}

/** A card's statement closing and due day: shown on the account row, edited in place. */
function StatementDays({ api, account, onSaved }: { api: Api; account: AccountView; onSaved: () => void }) {
  const s = account.statement!;
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(s.closingDay ? String(s.closingDay) : '');
  const [due, setDue] = useState(s.dueDaySource === 'set' && s.dueDay ? String(s.dueDay) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closingId = useId();
  const dueId = useId();

  const save = async (input: { closingDay?: number | null; dueDay?: number | null }) => {
    setBusy(true);
    setError(null);
    try {
      await api.setStatementDays(account.accountId, input);
      setEditing(false);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const day = (v: string) => (v.trim() ? Number(v) : null);
  const valid = (v: string) => !v.trim() || (/^\d{1,2}$/.test(v.trim()) && Number(v) >= 1 && Number(v) <= 31);

  const dueText = s.dueDay ? `due ${s.dueDaySource === 'payments' ? '~' : ''}${ordinal(s.dueDay)}${s.dueDaySource === 'payments' ? ' (from payments)' : ''}` : 'due day unknown';
  if (!editing) {
    return (
      <span className="stmt-days block">
        {s.closingDay ? <span>Statement closes on the {ordinal(s.closingDay)}</span> : <span className="st-warn">Closing day needed</span>}
        <span>· {dueText}</span>
        {!s.closingDay && s.suggestedClosingDay && (
          <button type="button" className="btn suggest" disabled={busy} onClick={() => void save({ closingDay: s.suggestedClosingDay })}>
            Use suggested {ordinal(s.suggestedClosingDay)}
          </button>
        )}
        <button type="button" className="btn" onClick={() => setEditing(true)} aria-label={`Edit statement days for ${account.name} ${maskLabel(account.mask)}`}>
          {s.closingDay ? 'Edit' : 'Set'}
        </button>
        {error && <span className="error-text">{error}</span>}
      </span>
    );
  }
  return (
    <form
      className="stmt-days block"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid(closing) && valid(due)) void save({ closingDay: day(closing), dueDay: day(due) });
      }}
    >
      <label htmlFor={closingId}>Closes on day</label>
      <input id={closingId} inputMode="numeric" value={closing} onChange={(e) => setClosing(e.target.value)} placeholder={s.suggestedClosingDay ? String(s.suggestedClosingDay) : ''} aria-invalid={!valid(closing)} />
      <label htmlFor={dueId}>Due on day</label>
      <input id={dueId} inputMode="numeric" value={due} onChange={(e) => setDue(e.target.value)} placeholder={s.inferredDueDay ? String(s.inferredDueDay) : ''} aria-invalid={!valid(due)} />
      <button type="submit" className="btn btn-primary" disabled={busy || !valid(closing) || !valid(due)}>
        Save
      </button>
      <button type="button" className="btn" onClick={() => setEditing(false)}>
        Cancel
      </button>
      <span className="small muted block">1 to 31; days past the end of a short month mean its last day. Leave due empty to use the day from payments.</span>
      {error && <span className="error-text">{error}</span>}
    </form>
  );
}

function Head({ lede, sync, action }: { lede?: string; sync?: string; action?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>Connections</h1>
        {lede && <p className="lede">{lede}</p>}
        {sync && <p className="small muted">{sync}</p>}
      </div>
      {action}
    </header>
  );
}

function changesText(c: AccountChanges): string {
  const parts = [
    c.added.length ? `added ${c.added.join(', ')}` : '',
    c.notShared.length ? `no longer sharing ${c.notShared.join(', ')} (its lines stay for the record)` : '',
    c.reshared.length ? `sharing ${c.reshared.join(', ')} again` : '',
  ].filter(Boolean);
  if (!parts.length) return 'no account changes.';
  const text = parts.join('; ');
  return `${text}.${c.added.length || c.reshared.length ? ' Pulling transactions now.' : ''}`;
}

interface MenuItem {
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

/**
 * A "⋯" button with a small menu (WAI-ARIA menu button). Enter, Space or Down
 * opens it on the first item, Up on the last; arrows, Home and End move;
 * Escape or Tab closes; focus goes back to the button; a click outside closes.
 */
function RowMenu({ label, items, disabled }: { label: string; items: MenuItem[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Fixed to the viewport, so the scrolling table card can't clip it.
  const [place, setPlace] = useState<{ top: number; right: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const id = useId();

  const close = (focusButton = true) => {
    setOpen(false);
    if (focusButton) button.current?.focus();
  };
  const openAt = (i: number) => {
    const r = button.current?.getBoundingClientRect();
    if (r) setPlace({ top: r.bottom + 4, right: Math.max(16, window.innerWidth - r.right) });
    setActive(i);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[active]?.focus({ preventScroll: true });
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    // Follow the button on scroll or resize; close once it's out of view.
    const onMove = () => {
      const r = button.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) setOpen(false);
      else setPlace({ top: r.bottom + 4, right: Math.max(16, window.innerWidth - r.right) });
    };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const onButtonKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAt(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openAt(items.length - 1);
    }
  };
  const onMenuKey = (e: ReactKeyboardEvent) => {
    const last = items.length - 1;
    if (e.key === 'ArrowDown') setActive((i) => (i === last ? 0 : i + 1));
    else if (e.key === 'ArrowUp') setActive((i) => (i === 0 ? last : i - 1));
    else if (e.key === 'Home') setActive(0);
    else if (e.key === 'End') setActive(last);
    else if (e.key === 'Escape') close();
    else if (e.key === 'Tab') close(false);
    else return;
    if (e.key !== 'Tab') e.preventDefault();
  };
  const choose = (item: MenuItem) => {
    close();
    item.onSelect();
  };

  return (
    <span className="row-menu">
      <button
        ref={button}
        type="button"
        className="menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? close() : openAt(0))}
        onKeyDown={onButtonKey}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <ul ref={menu} id={id} role="menu" aria-label={label} className="menu" style={place ?? undefined} onKeyDown={onMenuKey}>
          {items.map((item, i) => (
            <li role="none" key={item.label}>
              <button
                type="button"
                role="menuitem"
                tabIndex={i === active ? 0 : -1}
                className={item.danger ? 'danger' : undefined}
                onClick={() => choose(item)}
                onMouseEnter={() => setActive(i)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/** An inline yes/no, in place of a browser dialog. */
function Confirm({ text, yes, danger, busy, onYes, onNo }: { text: string; yes: string; danger?: boolean; busy: boolean; onYes: () => void; onNo: () => void }) {
  return (
    <div className="confirm" role="group" aria-label={text}>
      <span>{text}</span>
      <span className="buttons">
        <button type="button" className="btn" disabled={busy} onClick={onNo}>
          Cancel
        </button>
        <button type="button" className={danger ? 'btn btn-danger' : 'btn btn-dark'} disabled={busy} onClick={onYes}>
          {yes}
        </button>
      </span>
    </div>
  );
}

function accountsPhrase(accounts: AccountView[]): string {
  const names = accounts.map((a) => `${a.name} ${maskLabel(a.mask)}`.trim());
  if (names.length === 0) return 'this login';
  if (names.length <= 2) return names.join(' and ');
  return `${names[0]} and ${names.length - 1} more accounts`;
}

const SERVICE_STATUS: Record<string, { text: string; tone: string }> = {
  ok: { text: 'Connected', tone: 'ok' },
  not_configured: { text: 'Not set up', tone: 'off' },
  error: { text: 'Error', tone: 'err' },
  pending: { text: 'Waiting', tone: 'off' },
  login_required: { text: 'Sign in again', tone: 'warn' },
};

function SystemRow({ name, what, service }: { name: string; what: string; service: ServiceConnection | undefined }) {
  const s = service ? (SERVICE_STATUS[service.status] ?? { text: service.status, tone: 'off' }) : { text: 'Not checked yet', tone: 'off' };
  return (
    <div className="system">
      <div>
        <strong>{name}</strong>
        <span className="small muted">
          {what}
          {service?.detail ? ` · ${service.detail}` : ''}
          {service?.checkedAt ? ` · checked ${shortStamp(service.checkedAt)}` : ''}
        </span>
      </div>
      <span className={`status st-${s.tone}`}>{s.text}</span>
    </div>
  );
}

/** Both labels take the same space, so the button never changes width. */
function BusyLabel({ busy, busyText = 'Opening…', children }: { busy: boolean; busyText?: string; children: ReactNode }) {
  return (
    <span className="swap">
      <span className={busy ? 'off' : undefined} aria-hidden={busy || undefined}>
        {children}
      </span>
      <span className={busy ? undefined : 'off'} aria-hidden={!busy || undefined}>
        {busyText}
      </span>
    </span>
  );
}



/** Mock mode only: stands in for Plaid Link. No network, no real data. */
/** onSuccess gets the chosen seed user for a new login, or the institution for update mode. */
function MockLinkDialog({ link, onSuccess, onExit }: { link: SavedLink; onSuccess: (choice: string) => void; onExit: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [user, setUser] = useState(SEED_LOGINS[0].user);
  const titleId = useId();
  const update = link.mode === 'update';
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="mock-link"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onExit();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSuccess(update ? (link.institution ?? 'First Platypus Bank') : user);
        }}
      >
        <span className="mock-tag">Mock Plaid</span>
        <h2 id={titleId}>
          {link.manageAccounts ? `Choose ${link.institution ?? 'the bank'}'s accounts to share` : update ? `Sign in to ${link.institution ?? 'the bank'} again` : 'First Platypus Bank: choose a seeded sandbox user'}
        </h2>
        <p className="small muted">
          Mock mode stands in for Plaid Link here. Nothing leaves this computer.
          {link.manageAccounts ? ' Continuing stops sharing the last account, or shares it again if it was off.' : ''}
        </p>
        {!update && (
          <fieldset>
            <legend className="visually-hidden">Sandbox user</legend>
            {SEED_LOGINS.map((s) => (
              <label key={s.user} className="mock-bank">
                <input type="radio" name="bank" value={s.user} checked={user === s.user} onChange={() => setUser(s.user)} />
                <span>
                  {s.user} <span className="small muted">· {s.institution} · {s.accounts.map((a) => `${a.name} ${maskLabel(a.mask)}`).join(', ')}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <div className="buttons">
          <button type="button" className="btn" onClick={onExit}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Continue
          </button>
        </div>
      </form>
    </dialog>
  );
}
